/**
 * Runtime configuration utility
 * Supports both build-time (import.meta.env) and runtime (window.__ENV__) environment variables
 */

class Config {
  constructor() {
    this.env = {
      ...import.meta.env,
      ...(window.__ENV__ || {})
    };
  }

  /**
   * Get API URL with fallback logic
   * @returns {string} API base URL
   */
  getApiUrl() {
    const url = this.env.VITE_API_URL;
    if (!url || url === '') {
      console.error('Environment variables:', this.env);
      throw new Error('VITE_API_URL environment variable is required but not set. Check container environment configuration.');
    }
    return url;
  }

  /**
   * Get WebSocket URL
   * @returns {string} WebSocket URL
   */
  getWsUrl() {
    return this.env.VITE_WS_URL || this.getApiUrl().replace(/^http/, 'ws') + '/ws';
  }

  /**
   * Get auth token
   * @returns {string} Auth token
   * @throws {Error} If no valid auth token is available
   */
  getAuthToken() {
    if (window.TABBLY_AUTH_TOKEN) {
      return window.TABBLY_AUTH_TOKEN;
    }

    const envToken = this.env.VITE_AUTH_TOKEN;
    if (envToken && envToken !== '') {
      return envToken;
    }

    const lsToken = (typeof localStorage !== 'undefined') ? localStorage.getItem('auth_token') : null;
    if (lsToken && lsToken !== '') {
      return lsToken;
    }

    if (this.isDev()) {
      console.error('No authentication token available. Please set VITE_AUTH_TOKEN in your environment.');
    }

    throw new Error('No authentication token available. Device must be properly enrolled or VITE_AUTH_TOKEN must be set.');
  }

  /**
   * Get default restaurant ID
   * @returns {string} Default restaurant ID
   */
  getDefaultRestaurantId() {
    return this.env.VITE_DEFAULT_RESTAURANT_ID || '0886e20f-8826-419d-9117-336b30667404';
  }

  /**
   * Check if running in development mode
   * @returns {boolean} Is development mode
   */
  isDev() {
    return this.env.DEV || this.env.NODE_ENV === 'development';
  }

  /**
   * Get environment name
   * @returns {string} Environment name
   */
  getEnvironment() {
    return this.env.VITE_ENVIRONMENT || (this.isDev() ? 'development' : 'production');
  }

  /**
   * Get all environment variables (for debugging)
   * @returns {object} All environment variables
   */
  getAll() {
    return { ...this.env };
  }
}

export const config = new Config();
